import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, DialogActions, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';

import { closeDialog } from '@/components/Dialog';
import { useConnectedFormComponents } from '@/components/form';
import AppError from '@/interfaces/AppError';
import { IAgendaBlock } from '@/interfaces/maintenance/IAgendaBlock';
import maintenancesService from '@/services/maintenances';

type Form = {
  startDate: Date;
  endDate: Date;
  reason?: string;
};

const schema = Yup.object().shape({
  startDate: Yup.date()
    .typeError('Enter a valid date and time')
    .required('Start is required'),
  endDate: Yup.date()
    .typeError('Enter a valid date and time')
    .required('End is required')
    .min(Yup.ref('startDate'), 'End must be after the start'),
  reason: Yup.string(),
});

interface Props {
  onCreated(block: IAgendaBlock): void;
}

function startOfNextHour(): Date {
  const date = new Date();
  date.setMinutes(0, 0, 0);
  date.setHours(date.getHours() + 1);
  return date;
}

/**
 * Marks a period as unavailable so nothing can be booked into it. Kept separate
 * from the booking dialog: a block has no vehicle, customer or type, so folding
 * it into that form would make half its fields conditional.
 */
export default function AgendaBlockForm({ onCreated }: Props) {
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  const defaultStart = startOfNextHour();
  const defaultEnd = new Date(defaultStart.getTime() + 60 * 60 * 1000);

  const { handleSubmit, control, ...formHook } = useForm<Form>({
    defaultValues: { startDate: defaultStart, endDate: defaultEnd },
    resolver: yupResolver(schema),
  });

  const { TextInput, DateTimePicker } = useConnectedFormComponents<Form>({
    control,
  });

  const onSubmit = async (data: Form) => {
    try {
      setSubmitLoading(true);

      const block = await maintenancesService.createAgendaBlock({
        startDate: data.startDate,
        endDate: data.endDate,
        reason: data.reason,
      });

      onCreated(block);
      enqueueSnackbar('Agenda blocked', { variant: 'success' });
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        error instanceof AppError
          ? error?.message
          : 'Error blocking the agenda. Please, try again later',
        { variant: 'error' }
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <FormProvider control={control} handleSubmit={handleSubmit} {...formHook}>
      <Box
        sx={{ width: { sm: '450px' } }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Typography variant="body2" color="text.secondary">
          Nothing can be booked between these times. Use it for days the
          mechanic is unavailable, or for single slots.
        </Typography>

        <DateTimePicker
          fieldName="startDate"
          label="From"
          textFieldProps={{ required: true }}
        />

        <DateTimePicker
          fieldName="endDate"
          label="To"
          textFieldProps={{ required: true }}
        />

        <TextInput
          fieldName="reason"
          label="Reason"
          fullWidth
          helperText="Shown on the agenda and when a booking is refused"
        />

        <DialogActions>
          <Button onClick={closeDialog} disabled={submitLoading}>
            Cancel
          </Button>

          <Button
            type="submit"
            color="primary"
            disabled={submitLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {submitLoading ? 'Loading...' : 'Block'}
          </Button>
        </DialogActions>
      </Box>
    </FormProvider>
  );
}
